import * as fe from "@runn/filter-engine"
import { Action, ActionCreator, Dispatch } from "redux"

import {
  changeActivePage,
  isValidPage,
} from "./common/ActivePage/ActivePage.slice"
import { moveDateRange } from "./common/calendar.reducer"

import {
  setPeopleFilterSet,
  setPeopleWildSearch,
} from "./Planner/Planner.actions"
import { panelOpened } from "./Planner/reducer2/panelSlice"
import {
  filteredByProjects,
  filteredByTemplates,
} from "./Planner/reducer2/projectFilterSlice"
import { personIdSelected } from "./Planner/reducer2/schedulePreviewSlice"
import { setFocusedNote } from "./Planner/reducer2/noteSlice"

// Track combined filters
let combinedPeopleFilters = []

const config: Record<string, ActionCreator<Action>> = {
  activePage: (page: string) =>
    isValidPage(page) ? changeActivePage(page) : undefined,
  PersonSchedule: () => panelOpened("personSchedule"),
  SchedulePersonSelected: (id: string) => personIdSelected(parseInt(id, 10)),
  Project: (projectId: string) => {
    const id = parseInt(projectId, 10)
    return filteredByProjects([id])
  },
  Template: (templateId: string) => {
    const id = parseInt(templateId, 10)
    return filteredByTemplates([id])
  },
  Person: (personId: string) =>
    setPeopleFilterSet({
      name: "",
      filters: fe.filters.personId({ list: [parseInt(personId, 10)] }),
    }),
  PersonTeam: (teamId: string) =>
    setPeopleFilterSet({
      name: "",
      filters: fe.filters.personTeamId({ list: [parseInt(teamId, 10)] }),
    }),
  PersonRole: (roleIds: string) => {
    const roles = roleIds.split(",").map((id) => parseInt(id, 10))
    combinedPeopleFilters = [
      ...combinedPeopleFilters,
      fe.filters.personRoleId({ list: roles }),
    ]
    return undefined // Don't dispatch action yet
  },
  PersonProject: (projectIds: string) => {
    const ids = projectIds.split(",").map((id) => parseInt(id, 10))
    combinedPeopleFilters = [
      ...combinedPeopleFilters,
      fe.filters.personProjectId({ list: ids }),
    ]
    return undefined // Don't dispatch action yet
  },
  PersonProjectRole: (roleIds: string) => {
    const roles = roleIds.split(",").map((id) => parseInt(id, 10))
    combinedPeopleFilters = [
      ...combinedPeopleFilters,
      fe.filters.personProjectRoleId({ list: roles }),
    ]
    return undefined // Don't dispatch action yet
  },
  PersonWildSearch: (search: string) => setPeopleWildSearch(search),
  CalendarStartDate: (startDate: unknown) => {
    if (typeof startDate === "string") {
      return moveDateRange(new Date(startDate))
    }
    return undefined
  },
  People: (peopleIds: string) => {
    const ids = peopleIds.split(",").map((id) => parseInt(id, 10))
    return setPeopleFilterSet({
      name: "",
      filters: fe.filters.personId({ list: ids }),
    })
  },
  PersonNote: (noteId: string) => {
    return setFocusedNote({
      noteId: parseInt(noteId, 10),
      noteType: "person",
    })
  },
  ProjectNote: (noteId: string) => {
    return setFocusedNote({
      noteId: parseInt(noteId, 10),
      noteType: "project",
    })
  },
}

const loadStateFromSearchParams = (dispatch: Dispatch) => {
  const url = new URL(window.location.href)
  const searchParams = url.searchParams
  combinedPeopleFilters = [] // Reset combined filters

  for (const [key, actionCreator] of Object.entries(config)) {
    const value = searchParams.get(key)
    if (typeof value === "string") {
      const action = actionCreator(value)
      if (typeof action !== "undefined") {
        dispatch(action)
      }
    }
    searchParams.delete(key)
  }

  // Dispatch combined filters if any exist
  if (combinedPeopleFilters.length > 0) {
    dispatch(
      setPeopleFilterSet({
        name: "",
        filters: fe.filters.and(combinedPeopleFilters),
      }),
    )
  }

  window.history.replaceState(null, "", url)
}

export { loadStateFromSearchParams }
